import { useEffect, useState } from "react";
import { Link, useRouterState } from "@tanstack/react-router";
import { Heart, X } from "lucide-react";
import { useMembership } from "@/hooks/useMembership";
import { useHideCommerce } from "@/lib/native-app";

const DISMISS_KEY = "rt_account_waiting_dismissed";

/** Pages where the reminder would be redundant or intrusive. */
const SUPPRESSED_PREFIXES = [
  "/admin",
  "/auth",
  "/login",
  "/signup",
  "/checkout",
  "/choose-membership",
  "/complete-membership",
  "/register-membership",
  "/membership",
  "/pricing",
  "/billing",
];

export function shouldShowAccountWaiting(input: {
  isAuthed: boolean;
  isMember: boolean;
  isAdmin: boolean;
  loading: boolean;
  pathname: string;
  dismissed: boolean;
  hideCommerce: boolean;
}): boolean {
  if (!input.isAuthed || input.loading || input.isMember || input.isAdmin) return false;
  if (input.dismissed || input.hideCommerce) return false;
  return !SUPPRESSED_PREFIXES.some(
    (p) => input.pathname === p || input.pathname.startsWith(`${p}/`),
  );
}

/**
 * Gentle on-site reminder for signed-in visitors who registered but never
 * chose a paid membership. Dismissible for the browser session; hidden in the
 * iPhone app where purchasing is not offered.
 */
export function AccountWaitingBanner() {
  const { isAuthed, isMember, isAdmin, loading, error } = useMembership();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const hideCommerce = useHideCommerce();
  const [dismissed, setDismissed] = useState(true);

  useEffect(() => {
    try {
      setDismissed(sessionStorage.getItem(DISMISS_KEY) === "1");
    } catch {
      setDismissed(false);
    }
  }, []);

  if (error) return null;
  if (
    !shouldShowAccountWaiting({ isAuthed, isMember, isAdmin, loading, pathname, dismissed, hideCommerce })
  ) {
    return null;
  }

  const dismiss = () => {
    setDismissed(true);
    try {
      sessionStorage.setItem(DISMISS_KEY, "1");
    } catch {
      /* storage unavailable — dismissal lasts for this page view */
    }
  };

  return (
    <aside
      role="status"
      aria-label="Your account is waiting"
      className="mx-auto mt-3 w-full max-w-5xl px-4"
    >
      <div className="relative flex flex-col gap-3 rounded-2xl border border-primary/25 bg-primary/10 p-4 pr-11 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-start gap-3">
          <Heart className="mt-0.5 h-5 w-5 shrink-0 text-primary" aria-hidden />
          <div>
            <p className="font-semibold text-foreground">Your account is waiting 💛</p>
            <p className="text-sm text-muted-foreground">
              You're signed in — whenever you feel ready, choose a membership to open every
              tool, journal and game. No rush.
            </p>
          </div>
        </div>
        <Link
          to="/choose-membership"
          className="inline-flex min-h-11 shrink-0 items-center justify-center rounded-full bg-primary px-5 text-sm font-semibold text-primary-foreground hover:bg-primary/90"
        >
          Choose your membership
        </Link>
        <button
          type="button"
          onClick={dismiss}
          aria-label="Hide this reminder"
          className="absolute right-2 top-2 inline-flex h-9 w-9 items-center justify-center rounded-full text-muted-foreground hover:bg-background/60 hover:text-foreground"
        >
          <X className="h-4 w-4" aria-hidden />
        </button>
      </div>
    </aside>
  );
}
